import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowUp, Bot, ClipboardCopy, MessageSquarePlus, Sparkles, Square } from "lucide-react";
import { useApi } from "@ncp/api-client";
import { formatDateTime, useLocale, useTranslation } from "@ncp/i18n";
import { Button, Tooltip, cn, notifySaveFailed, copyText } from "@ncp/ui";
import type { AssessmentReference, DiscussionTurn } from "./api";
import { AssessmentBody } from "./AssessmentBody";

type StreamEvent = { type: "token"; text: string } | { type: "final"; turns: DiscussionTurn[] } | { type: "error"; message: string };

/**
 * A conversation with the review agent about one suggestion, where the suggestion is shown: quick questions to start (why this verdict,
 * what would raise the confidence, which evidence is weakest, what the finding must address), a field for anything else, and the answers
 * streamed in the same type as the assessment, citing its numbered references. The platform's text is never placed in the officer's
 * finding (7.12): there is no action that copies an answer into it, and the finding is written by the officer. The conversation is kept on
 * the case for whoever opens the criterion next.
 */
export function SuggestionChat({ caseId, criterionKey, references, readOnly, onOpenReference }: {
  caseId: string; criterionKey: string; references?: AssessmentReference[] | null; readOnly: boolean;
  onOpenReference?: (r: AssessmentReference) => void;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const api = useApi();
  const qc = useQueryClient();
  const key = ["review-discussion", caseId, criterionKey];
  const discussion = useQuery({ queryKey: key, queryFn: () => api.get<DiscussionTurn[]>(`/api/cases/${caseId}/review/discussions/${encodeURIComponent(criterionKey)}`) });
  const [draft, setDraft] = useState("");
  const [pending, setPending] = useState<{ question: string; answer: string } | null>(null);
  const abort = useRef<AbortController | null>(null);
  const field = useRef<HTMLTextAreaElement>(null);
  const end = useRef<HTMLDivElement>(null);
  useEffect(() => { setDraft(""); setPending(null); abort.current?.abort(); }, [criterionKey]);
  useEffect(() => { if (pending) end.current?.scrollIntoView({ block: "nearest" }); }, [pending?.answer.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const turns = discussion.data ?? [];
  const busy = pending !== null;
  const quick = [t("backoffice:review.discuss.why"), t("backoffice:review.discuss.raise"), t("backoffice:review.discuss.weakest"), t("backoffice:review.discuss.address")];

  const ask = async (question: string) => {
    const q = question.trim();
    if (q.length < 2 || busy) return;
    setDraft("");
    setPending({ question: q, answer: "" });
    const controller = new AbortController();
    abort.current = controller;
    try {
      let answer = "";
      for await (const e of api.streamEvents<StreamEvent>(`/api/cases/${caseId}/review/discussions/${encodeURIComponent(criterionKey)}/stream`, { question: q, locale }, controller.signal)) {
        if (e.type === "token") { answer += e.text; setPending({ question: q, answer }); }
        else if (e.type === "final") qc.setQueryData<DiscussionTurn[]>(key, (old) => [...(old ?? []), ...e.turns]);
        else throw new Error(e.message);
      }
    } catch (err) {
      // A stopped answer is not a failure; anything else is reported with its cause.
      notifySaveFailed(err, t("backoffice:review.discuss.failed"));
    } finally {
      setPending(null);
      abort.current = null;
      void qc.invalidateQueries({ queryKey: key });
    }
  };
  const restart = async () => {
    abort.current?.abort();
    try { await api.delete(`/api/cases/${caseId}/review/discussions/${encodeURIComponent(criterionKey)}`); } catch (e) { notifySaveFailed(e); return; }
    qc.setQueryData<DiscussionTurn[]>(key, []);
  };
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); void ask(draft); }
  };
  const copy = async (text: string) => { await copyText(text, t("backoffice:review.discuss.copied")); };

  return (
    <div className="mt-4 overflow-hidden rounded-xl border border-seablue-200 bg-white">
      <div className="flex items-center gap-2 border-b border-seablue-100 bg-seablue-50/60 px-4 py-2">
        <Sparkles className="size-4 text-seablue-700" aria-hidden />
        <p className="text-xs font-semibold text-seablue-950">{t("backoffice:review.discuss.title")}</p>
        <p className="hidden text-[11px] text-seablue-800/80 sm:block">· {t("backoffice:review.discuss.subtitle")}</p>
        {turns.length > 0 && !readOnly ? (
          <Tooltip content={t("backoffice:review.discuss.restart")}>
            <Button size="icon-sm" variant="ghost" className="ms-auto size-7 text-seablue-800" aria-label={t("backoffice:review.discuss.restart")} onClick={() => void restart()} disabled={busy}><MessageSquarePlus className="size-3.5" /></Button>
          </Tooltip>
        ) : null}
      </div>

      {turns.length > 0 || pending ? (
        <div className="max-h-[28rem] space-y-4 overflow-y-auto px-4 py-4 scrollbar-thin">
          {turns.map((turn, i) => (turn.role === "officer"
            ? <Question key={i} text={turn.text} meta={[turn.byName, formatDateTime(turn.at, locale)].filter(Boolean).join(" · ")} />
            : <Answer key={i} text={turn.text} references={references} onOpenReference={onOpenReference}
                actions={!turn.offline ? (
                  <Button size="sm" variant="ghost" className="h-7 text-sand-700" onClick={() => void copy(turn.text)}><ClipboardCopy className="size-3.5" /> {t("backoffice:review.discuss.copy")}</Button>
                ) : null} />))}
          {pending ? (
            <>
              <Question text={pending.question} />
              {pending.answer ? <Answer text={pending.answer} references={references} streaming /> : <Thinking />}
            </>
          ) : null}
          <div ref={end} />
        </div>
      ) : null}

      {!readOnly ? (
        <div className={cn("px-4 pb-3", turns.length > 0 || pending ? "border-t border-sand-100 pt-3" : "pt-3")}>
          {!busy ? (
            <div className="mb-2.5 flex flex-wrap gap-1.5">
              {quick.map((q) => (
                <button key={q} type="button" onClick={() => void ask(q)} className="rounded-full border border-seablue-200 bg-white px-2.5 py-1 text-[11px] font-medium text-seablue-900 transition-colors hover:border-seablue-400 hover:bg-seablue-50">{q}</button>
              ))}
            </div>
          ) : null}
          <div className="flex items-end gap-2 rounded-lg border border-sand-300 bg-white px-3 py-2 focus-within:border-seablue-400 focus-within:ring-2 focus-within:ring-seablue-100">
            <textarea ref={field} value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={onKey} rows={1} dir="auto"
              placeholder={t("backoffice:review.discuss.placeholder")} aria-label={t("backoffice:review.discuss.placeholder")}
              className="max-h-32 min-h-[1.5rem] flex-1 resize-none bg-transparent text-[13px] leading-6 text-aeblack-900 outline-none placeholder:text-sand-500 field-sizing-content" />
            {busy ? (
              <Button size="icon-sm" variant="ghost" className="size-7 shrink-0" aria-label={t("backoffice:review.discuss.stop")} onClick={() => abort.current?.abort()}><Square className="size-3.5" /></Button>
            ) : (
              <Button size="icon-sm" className="size-7 shrink-0 bg-seablue-700 text-white hover:bg-seablue-800" aria-label={t("backoffice:review.discuss.send")} disabled={draft.trim().length < 2} onClick={() => void ask(draft)}><ArrowUp className="size-3.5" /></Button>
            )}
          </div>
        </div>
      ) : turns.length === 0 ? <p className="px-4 py-3 text-xs text-muted-foreground">{t("backoffice:review.discuss.none")}</p> : null}
    </div>
  );
}

function Question({ text, meta }: { text: string; meta?: string }) {
  return (
    <div className="flex flex-col items-end">
      <p className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-ee-md bg-aegold-50 px-3.5 py-2 text-[13px] leading-6 text-aeblack-900 ring-1 ring-aegold-200" dir="auto">{text}</p>
      {meta ? <p className="mt-1 text-[10px] text-sand-500">{meta}</p> : null}
    </div>
  );
}

function Answer({ text, references, onOpenReference, streaming = false, actions }: { text: string; references?: AssessmentReference[] | null; onOpenReference?: (r: AssessmentReference) => void; streaming?: boolean; actions?: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="flex gap-2.5">
      <span className="mt-0.5 grid size-6 shrink-0 place-items-center rounded-full bg-seablue-100 text-seablue-800" aria-hidden><Bot className="size-3.5" /></span>
      <div className="min-w-0 flex-1">
        <p className="mb-0.5 text-[11px] font-semibold text-seablue-900">{t("backoffice:review.discuss.agent")}</p>
        <AssessmentBody markdown={text} references={references} onOpenReference={onOpenReference} compact />
        {streaming ? <span className="ms-0.5 inline-block h-3.5 w-1.5 animate-pulse rounded-sm bg-seablue-500 align-middle" aria-hidden /> : null}
        {actions ? <div className="mt-2 flex flex-wrap gap-1.5">{actions}</div> : null}
      </div>
    </div>
  );
}

function Thinking() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2.5 text-xs text-seablue-900">
      <span className="grid size-6 place-items-center rounded-full bg-seablue-100 text-seablue-800" aria-hidden><Bot className="size-3.5" /></span>
      <span className="flex items-center gap-1">{t("backoffice:review.discuss.thinking")}<span className="inline-flex gap-0.5">{[0, 1, 2].map((i) => <span key={i} className="size-1 animate-bounce rounded-full bg-seablue-500" style={{ animationDelay: `${i * 120}ms` }} />)}</span></span>
    </div>
  );
}
